<template>
  <a-card :bordered="false">
    <investment-order-detail-modal ref="detailModal" />
    <!-- Query area -->
    <div class="table-page-search-wrapper">
      <a-form layout="inline">
        <a-row :gutter="24">
          <a-col :md="6" :sm="24">
            <a-form-item :label="$t('date')">
              <a-range-picker
                v-model="queryParam.dateRange"
                style="width: 100%"
                format="DD-MM-YYYY"
                :placeholder="[$t('Startdate'), $t('Enddate')]"
                @change="onDateChange"
              />
            </a-form-item>
          </a-col>
          <a-col :md="5" :sm="24">
            <a-form-item :label="$t('investorList.tzddbh')">
              <a-input v-model="queryParam.orderNo" :placeholder="$t('enter')" />
            </a-form-item>
          </a-col>
          <a-col :md="6" :sm="24">
            <a-form-item :label="$t('investorList.tzzcbid')">
              <a-input v-model="queryParam.investAssetId" :placeholder="$t('enter')" />
            </a-form-item>
          </a-col>
          <a-col :md="5" :sm="24">
            <a-form-item :label="$t('investorList.tzrzh')">
              <a-input v-model="queryParam.investUserMobile" :placeholder="$t('enter')" />
            </a-form-item>
          </a-col>
          <a-col :md="1" :sm="24">
            <span class="table-page-search-submitButtons">
              <a-button type="primary" @click="handleSearch">{{ $t('search') }}</a-button>
            </span>
          </a-col>
        </a-row>
      </a-form>
    </div>

    <!-- Table area -->
    <a-table
      bordered
      ref="table"
      size="default"
      rowKey="id"
      :columns="columns"
      :dataSource="dataSource"
      :pagination="pagination"
      :loading="loading"
      :scroll="{ x: 'max-content' }"
      @change="handleTableChange"
    >
      <span slot="investStatus" slot-scope="text">
        {{ investStatusFilter(text) }}
      </span>
      <span slot="type" slot-scope="text">
        {{ typeFilter(text) }}
      </span>
      <span slot="investAmount" slot-scope="text">
        {{ text | FenToDong }}
      </span>
      <span slot="investYieldRate" slot-scope="text"> {{ text }}% </span>
      <span slot="expectIncome" slot-scope="text, record">
        {{ calcExpectIncome(record) | FenToDong }}
      </span>
      <span slot="action" slot-scope="text, record">
        <a @click="handleDetail(record)">{{ $t('investorList.xq') }}</a>
        <a-divider type="vertical" />
        <a :style="{ color: record.hasContract ? '' : '#bbb', cursor: record.hasContract ? 'pointer' : 'not-allowed' }" @click="record.hasContract && handleDownload(record)">{{ $t('investorList.DownloadInvestmentContract') }}</a>
        <a-divider type="vertical" />
        <a v-if="hasEditPermission" @click="handleEdit(record)">{{ $t('investorList.xgtzzt') }}</a>
      </span>
    </a-table>
    <asset-package-status-modal ref="assetPackageStatusModal" @success="loadData" />
  </a-card>
</template>

<script>
import { getInvestmentOrderList, downloadLoanContract } from '@/webpublicapi/vietnamBackstage'
import InvestmentOrderDetailModal from './modules/InvestmentOrderDetailModal'
import AssetPackageStatusModal from './modules/AssetPackageStatusModal.vue'
import { getPermissions } from '@/webpublicapi/login.js'

import lang from '@/i18n/lang'
import moment from 'moment'

export default {
  name: 'InvestmentOrders',
  components: {
    InvestmentOrderDetailModal,
    AssetPackageStatusModal,
  },
  data() {
    return {
      lang,
      hasEditPermission: false,
      // Query params
      queryParam: {
        dateRange: [moment().startOf('day').subtract(30, 'days'), moment().endOf('day')],
        begin: '',
        end: '',
        orderNo: '',
        investAssetId: '',
        investUserMobile: '',
      },
      dataSource: [],
      loading: false,
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total) => `${this.$t('components.total')} ${total} ${this.$t('components.items')}`,
      },
    }
  },
  computed: {
    columns() {
      return [
        { title: this.$t('investorList.tzddbh'), dataIndex: 'id', align: 'center' },
        { title: this.$t('investorList.tzrmc'), dataIndex: 'name', align: 'center' },
        { title: this.$t('investorList.tzrzh'), dataIndex: 'mobile', align: 'center' },
        {
          title: this.$t('investorList.tzrlx'),
          dataIndex: 'type',
          align: 'center',
          scopedSlots: { customRender: 'type' },
        },
        { title: this.$t('investorList.tzzcbid'), dataIndex: 'investId', align: 'center' },
        {
          title: this.$t('investorList.tzje'),
          dataIndex: 'investAmount',
          align: 'center',
          scopedSlots: { customRender: 'investAmount' },
        },
        {
          title: this.$t('investorList.tzrzt'),
          dataIndex: 'investStatus',
          align: 'center',
          scopedSlots: { customRender: 'investStatus' },
        },
        {
          title: this.$t('investorList.tzhbl'),
          dataIndex: 'investYieldRate',
          align: 'center',
          scopedSlots: { customRender: 'investYieldRate' },
        },
        { title: this.$t('investorList.tzzq'), dataIndex: 'investDuration', align: 'center' },
        {
          title: this.$t('investorList.yqsy'),
          dataIndex: 'expectIncome',
          align: 'center',
          scopedSlots: { customRender: 'expectIncome' },
        },
        { title: this.$t('investorList.ptffw'), dataIndex: 'platformServiceAmount', align: 'center' },
        { title: this.$t('investorList.tzsj'), dataIndex: 'createTime', align: 'center' },
        {
          title: this.$t('investorList.cz'),
          dataIndex: 'action',
          align: 'center',
          fixed: 'right',
          scopedSlots: { customRender: 'action' },
        },
      ]
    },
  },
  created() {
    this.loadData()
    this.getPermissions()
  },
  methods: {
    // Status filter
    investStatusFilter(status) {
      const statusMap = {
        '-3': this.$t('platformCollection.fund_processing_failed'),
        '-2': this.$t('platformCollection.cancelled'),
        '-1': this.$t('platformCollection.bid_failed'),
        0: this.$t('platformCollection.fund_processing'),
        1: this.$t('platformCollection.waiting_for_full_bid'),
        2: this.$t('platformCollection.full_bid'),
        3: this.$t('platformCollection.fund_processing'),
        4: this.$t('platformCollection.fund_processing_completed'),
        5: this.$t('platformCollection.target_completed'),
      }
      return statusMap[status] || this.$t('platformCollection.unknown')
    },
    // Type filter
    typeFilter(type) {
      const typeMap = {
        1: this.$t('investorList.gr'),
        10: this.$t('investorList.qy'),
      }
      return typeMap[type] || this.$t('platformCollection.unknown')
    },
    onDateChange(dates, dateStrings) {
      this.queryParam.begin = dateStrings[0]
      this.queryParam.end = dateStrings[1]
    },
    handleSearch() {
      this.pagination.current = 1
      this.loadData()
    },
    handleTableChange(pagination) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.loadData()
    },
    loadData() {
      this.loading = true

      // If no start/end time selected, default query recent 30 days
      if (!this.queryParam.begin && !this.queryParam.end) {
        const end = moment().endOf('day')
        const begin = moment().startOf('day').subtract(30, 'days')
        this.queryParam.begin = begin.format('YYYY-MM-DD')
        this.queryParam.end = end.format('YYYY-MM-DD')
      }
      const rawParams = {
        pageNo: this.pagination.current,
        pageSize: this.pagination.pageSize,
        begin: this.transformTime(this.queryParam.dateRange[0], 0) + ' 00:00:00',
        end: this.transformTime(this.queryParam.dateRange[1], 0) + ' 23:59:59',
        orderNo: this.queryParam.orderNo,
        investAssetId: this.queryParam.investAssetId,
        investUserMobile: this.queryParam.investUserMobile,
        hasContract: true,
      }

      // Do not send when field value is empty (avoid sending empty strings, etc.)
      const params = Object.keys(rawParams).reduce((acc, key) => {
        const val = rawParams[key]
        const shouldDrop = val === '' || val === null || val === undefined
        if (!shouldDrop) acc[key] = val
        return acc
      }, {})

      getInvestmentOrderList(params)
        .then((res) => {
          if (res.success || res.respCode === 1000 || res.code === 200) {
            const data = res.respData || res.result || {}
            this.dataSource = data.records || []
            this.dataSource.forEach((item) => {
              item.createTime = this.transformTime(item.createTime, 2)
            })
            this.pagination.total = data.total || 0
          } else {
            this.$message.warning(res.respMsg || res.message || this.$t('platformCollection.query_failed'))
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    calcExpectIncome(record) {
      const amount = Number(record && record.investAmount)
      const rate = Number(record && record.investYieldRate)
      if (!Number.isFinite(amount) || !Number.isFinite(rate)) return 0
      // Expected return = Principal x (1 + Return rate/100)
      return amount * (1 + rate / 100)
    },
    handleDetail(record) {
      this.$refs.detailModal.show(record)
    },
    handleEdit(record) {
      this.$refs.assetPackageStatusModal.show(record)
    },
    handleDownload(record) {
      const hide = this.$message.loading(this.$t('investorList.downloading') || 'Downloading, please wait...', 0)
      downloadLoanContract({ investAssetOrderId: record.id })
        .then((res) => {
          hide()
          // Determine file suffix based on response type
          const blobType = res?.type || ''
          let fileName = (record.name || 'contract') + '_' + (record.id || '')
          if (blobType.includes('zip') || blobType.includes('octet-stream') || blobType.includes('gzip')) {
            fileName += '.zip'
          } else if (blobType.includes('pdf')) {
            fileName += '.pdf'
          } else {
            fileName += '.zip'
          }
          const blob = new Blob([res], { type: blobType })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = fileName
          link.click()
          window.URL.revokeObjectURL(url)
        })
        .catch(() => {
          hide()
          this.$message.error(this.$t('investorList.download_failed'))
        })
    },
    getPermissions() {
      getPermissions().then((res) => {
        if (res.code === 1000 && res.data) {
          const hasPermission = res.data.some(
            (item) => item.key === 'com.weilu.mng.controller.InvestController@updateInvestOrderStatus'
          )
          this.hasEditPermission = hasPermission
        }
      })
    },
  },
}
</script>

<style lang="less" scoped>
@import '~@/assets/less/common.less';

.table-page-search-wrapper {
  margin-bottom: 16px;
}
</style>
